import { isToday, format } from "date-fns";
import { toDayScaleMinutes } from "./dayScale";
import { Booking, PricingSettings, TimeSlot, DailySchedule, LaneTimeSlot } from "../types";

/** A hét napjai hétfőtől vasárnapig: a `settings/pricing` napjai, a felületen ebben a sorrendben. */
export const WEEKDAYS = [
  "monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday",
] as const satisfies readonly (keyof PricingSettings)[];
export type Weekday = (typeof WEEKDAYS)[number];

/** A napok neve a felületen. Folyó szövegben kisbetűvel: `.toLowerCase()`. */
export const WEEKDAY_LABELS: Record<Weekday, string> = {
  monday: "Hétfő",
  tuesday: "Kedd",
  wednesday: "Szerda",
  thursday: "Csütörtök",
  friday: "Péntek",
  saturday: "Szombat",
  sunday: "Vasárnap",
};

/** A dátum hét napja, helyi idő szerint (ahogy a naptár adja): a `getDay()` 0-ja a vasárnap. */
export const weekdayOf = (date: Date): Weekday => WEEKDAYS[(date.getDay() + 6) % 7];

// A hét körbeér: a vasárnap után a hétfő jön (a vasárnap éjfél utáni órái a hétfőhöz tartozhatnak)
export const nextDayOf = (day: Weekday): Weekday => WEEKDAYS[(WEEKDAYS.indexOf(day) + 1) % WEEKDAYS.length];

/**
 * Az éjfél utáni órák (a 24., 25., ... óra, kulcsuk "0", "1", ...) a KÖVETKEZŐ nap táblájából
 * árazódnak-e: igen, ha az nyitva van és 0 órakor nyit, különben az aznapi tábla ugyanilyen
 * kulcsairól. Az egyetlen szabály erre: mindkét árszámítás (`getNormalHourlyRate`) és az
 * árbeállítás űrlapja (`pricingFormSchema.ts`) ezt kérdezi, a szerver tükre a
 * `functions/src/lib/pricing.js` azonos nevű függvénye. A hiányzó `isOpen` zárt napot jelent.
 * Típus-őr is: igaz ágon a következő nap biztosan megvan. (Generikus, mert az űrlap napja nem
 * `DailySchedule`: ott egy díj még üres is lehet.)
 */
export const midnightBelongsToNextDay = <T extends Pick<DailySchedule, "isOpen" | "openingHour">>(
  nextDaySchedule: T | null | undefined,
): nextDaySchedule is T => Boolean(nextDaySchedule && nextDaySchedule.isOpen && nextDaySchedule.openingHour === 0);

// Időpontok generálása
export const generateTimeSlots = (
  date: Date,
  bookings: Booking[],
  pricing: PricingSettings
): TimeSlot[] => {
  const slots: TimeSlot[] = [];
  
  // A hét napjának meghatározása
  const day = weekdayOf(date);
  const daySchedule = pricing[day];
  
  // Ellenőrizzük a kinyitásokat az adott napra
  const dateString = format(date, "yyyy-MM-dd");
  const dayOpenings = (pricing.closures || []).filter(closure => 
    closure.date === dateString && (closure.type || 'closure') === 'opening'
  );
  
  // Ha a nap alapból zárva van, csak akkor térünk vissza üres tömbbel, ha nincs kinyitás
  const hasOpening = dayOpenings.length > 0;
  if (!daySchedule.isOpen && !hasOpening) {
    return [];
  }
  
  let openingHour = daySchedule.openingHour;
  let closingHour = daySchedule.closingHour;
  
  // Ellenőrizzük a kinyitásokat, amelyek módosíthatják vagy felülírhatják a nyitvatartást
  dayOpenings.forEach(opening => {
    // Ha a kinyitásnak van konkrét kezdési és befejezési ideje, akkor az felülírja az eredeti nyitvatartást
    if (opening.startTime && opening.endTime) {
      const openingStartHour = parseInt(opening.startTime.split(":")[0]);
      let openingEndHour = parseInt(opening.endTime.split(":")[0]);
      
      // Ha éjfél utáni időpont (pl. 01:00), akkor 24+ órához konvertáljuk
      if (openingEndHour >= 0 && openingEndHour < 6) {
        openingEndHour += 24;
      }
      
      // TELJES FELÜLÍRÁS: A kinyitás időpontjai teljesen felülírják az eredeti nyitvatartást
      openingHour = openingStartHour;
      closingHour = openingEndHour;
    } else {
      // Ha csak részleges időpontok vannak megadva, akkor kiterjesztjük a nyitvatartást
      if (opening.startTime) {
        const openingStartHour = parseInt(opening.startTime.split(":")[0]);
        
        // Ha a kinyitás korábban kezdődik, mint az alapvető nyitvatartás
        if (openingStartHour < openingHour) {
          openingHour = openingStartHour;
        }
      }
      
      if (opening.endTime) {
        let openingEndHour = parseInt(opening.endTime.split(":")[0]);
        
        // Ha éjfél utáni időpont (pl. 01:00), akkor 24+ órához konvertáljuk
        if (openingEndHour >= 0 && openingEndHour < 6) {
          openingEndHour += 24;
        }
        
        // Ha a kinyitás később végződik, mint az alapvető nyitvatartás
        if (openingEndHour > closingHour) {
          closingHour = openingEndHour;
        }
      }
    }
  });
  
  // Foglalt pályák időpontonként
  const bookedLanes: Record<string, number[]> = {};
  
  // Foglalások feldolgozása
  bookings.forEach(booking => {
    // Ellenőrizzük, hogy a foglalás tartalmaz-e lanes mezőt
    if (!booking.lanes || !Array.isArray(booking.lanes)) {
      return; // Átugorjuk ezt a foglalást
    }
    
    const startHour = parseInt(booking.startTime.split(":")[0]);
    let endHour = parseInt(booking.endTime.split(":")[0]);
    
    // Kezelni kell az éjfélen túli nyitvatartás esetét
    if (endHour < startHour) {
      endHour += 24; // Hozzáadunk 24 órát, ha éjfélen túli időpontról van szó
    }
    
    // Minden órára a kezdő és befejező idő között
    for (let hour = startHour; hour < endHour; hour++) {
      // 24 órán túli órák kezelése
      const normalizedHour = hour >= 24 ? hour - 24 : hour;
      const timeKey = formatTimeForDisplay(normalizedHour);
      
      if (!bookedLanes[timeKey]) {
        bookedLanes[timeKey] = [];
      }
      
      // Hozzáadjuk a foglalt pályákat
      booking.lanes.forEach(lane => {
        if (!bookedLanes[timeKey].includes(lane)) {
          bookedLanes[timeKey].push(lane);
        }
      });
    }
  });
  
  // Zárások ellenőrzése az adott napra (csak 'closure' típusúak)
  const closures = pricing.closures || [];
  const dayClosures = closures.filter(closure => 
    closure.date === dateString && (closure.type || 'closure') === 'closure'
  );
  
  // Az aktuális idő meghatározása
  const now = new Date();
  const currentHour = now.getHours();
  
  // Időpontok generálása a nyitvatartási időn belül
  for (let hour = openingHour; hour < closingHour; hour++) {
    // Ha a kiválasztott nap a mai nap és az időpont már elmúlt, ne jelenítse meg
    if (isToday(date) && hour <= currentHour) {
      continue;
    }
    
    const timeKey = formatTimeForDisplay(hour);
    
    // Kezdjük az elérhető pályákkal
    let availableLanes = [1, 2, 3, 4].filter(lane => 
      !bookedLanes[timeKey] || !bookedLanes[timeKey].includes(lane)
    );
    
    // Ellenőrizzük a zárásokat és kinyitásokat
    
    // Ha a nap alapvetően zárva van, de van kinyitás, akkor ellenőrizzük a kinyitási időszakokat
    if (!daySchedule.isOpen && hasOpening) {
      // Alapértelmezésben minden pálya zárva
      availableLanes = [];
      
      // Ellenőrizzük a kinyitásokat
      dayOpenings.forEach(opening => {
        // Egész napos kinyitás
        if (!opening.startTime && !opening.endTime) {
          if (!opening.lanes) {
            // Minden pálya nyitva
            availableLanes = [1, 2, 3, 4].filter(lane => 
              !bookedLanes[timeKey] || !bookedLanes[timeKey].includes(lane)
            );
          } else {
            // Csak bizonyos pályák nyitva
            const openLanes = opening.lanes.filter(lane => 
              !bookedLanes[timeKey] || !bookedLanes[timeKey].includes(lane)
            );
            availableLanes = [...new Set([...availableLanes, ...openLanes])];
          }
        } else if (opening.startTime && opening.endTime) {
          // Időszakos kinyitás - ellenőrizzük, hogy ez az óra érintett-e
          const openingStartHour = parseInt(opening.startTime.split(":")[0]);
          let openingEndHour = parseInt(opening.endTime.split(":")[0]);
          
          // Kezelni kell az éjfélen túli kinyitást (0-5 óra → 24-29)
          if (openingEndHour >= 0 && openingEndHour < 6 && openingStartHour >= 6) {
            openingEndHour += 24;
          }
          
          // A hour már 24+ formátumban van az éjfél utáni óráknál (for ciklusból)
          // Egyszerű összehasonlítás
          if (hour >= openingStartHour && hour < openingEndHour) {
            
            if (!opening.lanes) {
              // Minden pálya nyitva
              availableLanes = [1, 2, 3, 4].filter(lane => 
                !bookedLanes[timeKey] || !bookedLanes[timeKey].includes(lane)
              );
            } else {
              // Csak bizonyos pályák nyitva
              const openLanes = opening.lanes.filter(lane => 
                !bookedLanes[timeKey] || !bookedLanes[timeKey].includes(lane)
              );
              availableLanes = [...new Set([...availableLanes, ...openLanes])];
            }
          }
        }
      });
    }
    
    // Ellenőrizzük a zárásokat
    dayClosures.forEach(closure => {
      // Egész napos zárás
      if (!closure.startTime && !closure.endTime) {
        if (!closure.lanes) {
          // Minden pálya zárva
          availableLanes = [];
        } else {
          // Csak bizonyos pályák zárva
          availableLanes = availableLanes.filter(lane => !closure.lanes!.includes(lane));
        }
      } else if (closure.startTime && closure.endTime) {
        // Időszakos zárás - ellenőrizzük, hogy ez az óra érintett-e
        const closureStartHour = parseInt(closure.startTime.split(":")[0]);
        let closureEndHour = parseInt(closure.endTime.split(":")[0]);
        
        // Kezelni kell az éjfélen túli zárást (0-5 óra → 24-29)
        if (closureEndHour >= 0 && closureEndHour < 6 && closureStartHour >= 6) {
          closureEndHour += 24;
        }
        
        // A hour már 24+ formátumban van az éjfél utáni óráknál (for ciklusból)
        // Egyszerű összehasonlítás
        if (hour >= closureStartHour && hour < closureEndHour) {
          
          if (!closure.lanes) {
            // Minden pálya zárva
            availableLanes = [];
          } else {
            // Csak bizonyos pályák zárva
            availableLanes = availableLanes.filter(lane => !closure.lanes!.includes(lane));
          }
        }
      }
    });
    
    slots.push({
      time: timeKey,
      available: availableLanes.length > 0,
      lanes: availableLanes
    });
  }
  
  return slots;
};

// Az éjfél utáni órák VÉGSŐ tartaléka: az aznapi "23"-as, ha az nincs, a "22"-es óradíj. Csak
// olyan éjfél utáni órára jut, amelynek sehol nincs tárolt díja. A 0 itt „nincs megadva”, nem
// „ingyen” (az admin űrlapja az üresen hagyott mezőt 0-ként menti), ezért a "23": 0 is a
// "22"-esre esik tovább; egy óra SAJÁT 0-s díja viszont 0 Ft marad. A szerver tükre:
// `functions/src/lib/pricing.js` (`lastHourRate`), a két oldalt a `pricingParity.test.js` őrzi.
const getLastHourRate = (hourlyRates: DailySchedule["hourlyRates"]): number => {
  const rates = hourlyRates as Record<string, number>;
  return rates["23"] || rates["22"] || 0;
};

// Ár kiszámítása
export const calculatePrice = (
  date: Date,
  startTime: string,
  endTime: string,
  laneCount: number,
  shoesRental: number,
  pricing: PricingSettings
): number => {
  // Időtartam kiszámítása
  const startHour = parseInt(startTime.split(":")[0]);
  let endHour = parseInt(endTime.split(":")[0]);
  
  // Kezelni kell az éjfélen túli nyitvatartás esetét
  if (endHour < startHour) {
    endHour += 24; // Hozzáadunk 24 órát, ha éjfélen túli időpontról van szó
  }
  
  // Alapárak
  let totalPrice = 0;
  
  // Az aktuális nap
  const day = weekdayOf(date);
  const daySchedule = pricing[day];
  
  // A következő nap (éjfél utáni foglaláshoz)
  const nextDaySchedule = pricing[nextDayOf(day)];
  
  // Ellenőrizzük a kinyitásokat az adott napra az ár számításához
  const dateString = format(date, "yyyy-MM-dd");
  const dayOpenings = (pricing.closures || []).filter(closure => 
    closure.date === dateString && (closure.type || 'closure') === 'opening'
  );
  
  // Ha a nap zárva van és nincs kinyitás, akkor 0 Ft a végösszeg
  const hasOpening = dayOpenings.length > 0;
  if (!daySchedule.isOpen && !hasOpening) {
    return 0;
  }
  
  // Ellenőrizzük, hogy van-e egyedi árazás a kinyitásban
  const customPricingOpening = dayOpenings.find(opening => 
    opening.hourlyRates && Object.keys(opening.hourlyRates).length > 0
  );
  
  // Az utolsó óra árát kikeressük fallback értékként az éjfél utáni órákhoz
  const lastHourRate = getLastHourRate(daySchedule.hourlyRates);

  // Minden órára külön számoljuk az árat
  for (let hour = startHour; hour < endHour; hour++) {
    let hourlyRate: number;

    if (customPricingOpening && customPricingOpening.hourlyRates) {
      const customRate = customPricingOpening.hourlyRates[hour.toString()];

      if (customRate !== undefined && customRate > 0) {
        hourlyRate = customRate;
      } else {
        hourlyRate = getNormalHourlyRate(hour, daySchedule, nextDaySchedule, lastHourRate);
      }
    } else {
      hourlyRate = getNormalHourlyRate(hour, daySchedule, nextDaySchedule, lastHourRate);
    }

    totalPrice += hourlyRate * laneCount;
  }

  // Normál óránkénti ár számítás függvény
  function getNormalHourlyRate(hour: number, daySchedule: DailySchedule, nextDaySchedule: DailySchedule | null, lastHourRate: number): number {
    if (hour < 24) {
      // Normál eset, az aktuális nap árát használjuk
      const hourForRate = hour;
      const hourlyRates = daySchedule.hourlyRates as Record<string, number>;
      return hourlyRates[hourForRate.toString()] || 0;
    } else {
      // Éjfél utáni óra - lehet az aktuális napban vagy a következő napban eltárolva
      const hourForRate = hour - 24;

      // Először nézzük meg, hogy a következő nap nyit-e 0 órakor
      if (midnightBelongsToNextDay(nextDaySchedule)) {
        // A következő nap 0 órakor nyit, az éjfél utáni óra ára a következő napban van eltárolva
        const nextDayHourlyRates = nextDaySchedule.hourlyRates as Record<string, number>;
        const hourKeyExists = Object.prototype.hasOwnProperty.call(nextDayHourlyRates, hourForRate.toString());

        if (hourKeyExists) {
          return nextDayHourlyRates[hourForRate.toString()];
        }
      } else {
        // A következő nap nem nyit 0 órakor, az éjfél utáni óra ára az aktuális napban van eltárolva
        const currentDayHourlyRates = daySchedule.hourlyRates as Record<string, number>;
        const hourKeyExists = Object.prototype.hasOwnProperty.call(currentDayHourlyRates, hourForRate.toString());

        if (hourKeyExists) {
          return currentDayHourlyRates[hourForRate.toString()];
        }
      }

      // Ha nem találtuk meg az árat, használjuk az aktuális nap utolsó órájának árát fallback-ként
      return lastHourRate;
    }
  }

  // Cipőbérlés hozzáadása
  if (shoesRental > 0) {
    const shoesPrice = shoesRental * pricing.shoesRentalPrice;
    totalPrice += shoesPrice;
  }

  return Math.round(totalPrice);
};

// Szükséges pályák számának kiszámítása
export const calculateRequiredLanes = (
  guestCount: number,
  daySchedule?: { minGuestsPerLane: number; maxGuestsPerLane: number }
): number => {
  // Alapértelmezett értékek, ha nincs megadva daySchedule
  const minGuestsPerLane = daySchedule?.minGuestsPerLane || 1;
  const maxGuestsPerLane = daySchedule?.maxGuestsPerLane || 7;
  
  // Legalább 1 pálya, de maximum 4 pálya lehet
  const requiredLanes = Math.ceil(guestCount / maxGuestsPerLane);
  
  // Ellenőrizzük, hogy a vendégek száma eléri-e a minimum vendégszámot
  // Ha nem, akkor is legalább 1 pályát kell foglalni
  if (guestCount < minGuestsPerLane) {
    return 1;
  }
  
  return Math.min(Math.max(requiredLanes, 1), 4);
};

// Idő formázása felhasználóbarát módon
export const formatTimeForDisplay = (hour: number): string => {
  // Érvénytelen óra értékek kezelése
  if (typeof hour !== 'number' || isNaN(hour) || hour < 0) {
    return "00:00"; // Alapértelmezett érték hibaesetre
  }

  if (hour === 24) return "00:00";
  if (hour === 25) return "01:00";
  if (hour > 25) return `${(hour - 24).toString().padStart(2, "0")}:00`;
  return `${hour.toString().padStart(2, "0")}:00`;
};

// ÚJ: Ár kiszámítása több pálya-idő kombináció alapján
export const calculatePriceForLaneTimeSlots = (
  date: Date,
  laneTimeSlots: Array<{ lane: number; startTime: string; endTime: string }>,
  shoesRental: number,
  pricing: PricingSettings
): number => {
  let totalPrice = 0;

  // A hét napjának meghatározása
  const day = weekdayOf(date);
  const daySchedule = pricing[day];

  // A következő nap (éjfél utáni foglaláshoz)
  const nextDaySchedule = pricing[nextDayOf(day)];

  // Ellenőrizzük a kinyitásokat az adott napra
  const dateString = format(date, "yyyy-MM-dd");
  const dayOpenings = (pricing.closures || []).filter(closure =>
    closure.date === dateString && (closure.type || 'closure') === 'opening'
  );

  // Ha a nap zárva van és nincs kinyitás, akkor 0 Ft a végösszeg
  const hasOpening = dayOpenings.length > 0;
  if (!daySchedule.isOpen && !hasOpening) {
    return 0;
  }

  // Ellenőrizzük, hogy van-e egyedi árazás a kinyitásban
  const customPricingOpening = dayOpenings.find(opening =>
    opening.hourlyRates && Object.keys(opening.hourlyRates).length > 0
  );

  // Az utolsó óra árát kikeressük fallback értékként az éjfél utáni órákhoz
  const lastHourRate = getLastHourRate(daySchedule.hourlyRates);

  // Minden pálya-idő kombinációra számoljuk az árat
  laneTimeSlots.forEach(slot => {
    const startHour = parseInt(slot.startTime.split(":")[0]);
    let endHour = parseInt(slot.endTime.split(":")[0]);

    // Kezelni kell az éjfélen túli nyitvatartás esetét
    if (endHour < startHour) {
      endHour += 24;
    }

    // Minden órára külön számoljuk az árat
    for (let hour = startHour; hour < endHour; hour++) {
      let hourlyRate: number;

      if (customPricingOpening && customPricingOpening.hourlyRates) {
        const customRate = customPricingOpening.hourlyRates[hour.toString()];

        if (customRate !== undefined && customRate > 0) {
          hourlyRate = customRate;
        } else {
          hourlyRate = getNormalHourlyRate(hour, daySchedule, nextDaySchedule, lastHourRate);
        }
      } else {
        hourlyRate = getNormalHourlyRate(hour, daySchedule, nextDaySchedule, lastHourRate);
      }

      // Egy pályára, egy órára
      totalPrice += hourlyRate;
    }
  });

  // Segédfüggvény a normál óránkénti ár számításhoz
  function getNormalHourlyRate(
    hour: number,
    daySchedule: DailySchedule,
    nextDaySchedule: DailySchedule | null,
    lastHourRate: number
  ): number {
    if (hour < 24) {
      const hourlyRates = daySchedule.hourlyRates as Record<string, number>;
      return hourlyRates[hour.toString()] || 0;
    } else {
      const hourForRate = hour - 24;

      if (midnightBelongsToNextDay(nextDaySchedule)) {
        const nextDayHourlyRates = nextDaySchedule.hourlyRates as Record<string, number>;
        if (Object.prototype.hasOwnProperty.call(nextDayHourlyRates, hourForRate.toString())) {
          return nextDayHourlyRates[hourForRate.toString()];
        }
      } else {
        const currentDayHourlyRates = daySchedule.hourlyRates as Record<string, number>;
        if (Object.prototype.hasOwnProperty.call(currentDayHourlyRates, hourForRate.toString())) {
          return currentDayHourlyRates[hourForRate.toString()];
        }
      }

      return lastHourRate;
    }
  }

  // Cipőbérlés hozzáadása
  if (shoesRental > 0) {
    const shoesPrice = shoesRental * pricing.shoesRentalPrice;
    totalPrice += shoesPrice;
  }

  return Math.round(totalPrice);
}; 


// Két időablak átfedésének vizsgálata
const laneSlotsOverlap = (
  start1: string,
  end1: string,
  start2: string,
  end2: string
): boolean => {
  // A nap skáláján (`dayScale.ts`): az éjfél utáni órák a nap végére kerülnek, így a
  // 22:00-01:00 típusú foglalások átfedése is helyesen jön ki
  const s1 = toDayScaleMinutes(start1);
  const e1 = toDayScaleMinutes(end1);
  const s2 = toDayScaleMinutes(start2);
  const e2 = toDayScaleMinutes(end2);

  return s1 < e2 && e1 > s2;
};

// Megmondja, hogy egy pálya-idő cellát foglal-e valamelyik megadott foglalás.
// A foglaltsági táblázat és a szerkesztő űrlap közösen használja, hogy a rácson
// látott foglaltság és a kijelölés érvényessége ne térhessen el egymástól.
export const isLaneTimeSlotBooked = (
  slot: LaneTimeSlot,
  bookings: Booking[]
): boolean => {
  return bookings.some((booking) => {
    // Új formátum: laneTimeSlots
    if (booking.laneTimeSlots && booking.laneTimeSlots.length > 0) {
      return booking.laneTimeSlots.some(
        (bookedSlot) =>
          bookedSlot.lane === slot.lane &&
          laneSlotsOverlap(
            slot.startTime,
            slot.endTime,
            bookedSlot.startTime,
            bookedSlot.endTime
          )
      );
    }

    // Régi formátum kompatibilitás: lanes + startTime/endTime
    if (booking.lanes && booking.lanes.includes(slot.lane)) {
      return laneSlotsOverlap(
        slot.startTime,
        slot.endTime,
        booking.startTime,
        booking.endTime
      );
    }

    return false;
  });
};

// ---------------------------------------------------------------------------
// Ár megjelenítése
//
// Minden összeget hármas csoportokra tagolunk: 5 000, 12 500, 1 234 567. A
// toLocaleString('hu-HU') a négyjegyűeket egyben hagyná (5000), így egy árlistában a
// 5000 és a 12 500 nem állna egymás alatt. A csoportok közé és a "Ft" elé nem törő
// szóköz kerül, hogy az összeg sortörésnél se essen szét. A szerver e-mail sablonjai
// ugyanezt teszik (functions/src/lib/pricing.js -> formatPrice) - tartsd szinkronban.
const NBSP = "\u00a0";

export const formatNumber = (value: number): string => {
  const rounded = Math.round(value);
  const grouped = Math.abs(rounded).toString().replace(/\B(?=(\d{3})+$)/g, NBSP);
  return rounded < 0 ? `-${grouped}` : grouped;
};

export const formatPrice = (value: number): string => `${formatNumber(value)}${NBSP}Ft`;
